import { Injectable } from '@angular/core';
import { OnboardingApiService } from './onboarding-api.service';
import { StateManagementService } from './state-management.service';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StripeService {

  constructor(
    private api: OnboardingApiService,
    private state: StateManagementService,
  ) {}

  /**
   * Creates a Stripe Connect Express account for the organization if one
   * doesn't already exist, then updates state with the new account ID.
   */
  createAndSaveAccount(organizationId: string): Observable<{ accountId: string }> {
    const org = this.state.snapshot.organization;
    const email = org?.Email || '';
    const businessName = org?.Name || 'Organization';

    return this.api.createStripeConnectAccount(organizationId, email, businessName).pipe(
      tap(result => {
        this.state.setStripeAccount(result.accountId, 'pending');
      })
    );
  }

  /**
   * Generates a Stripe hosted onboarding URL and redirects the browser there.
   * The app passes its current URL as both return and refresh URLs so Stripe
   * sends the user back after completing (or needing to resume) the flow.
   */
  redirectToOnboarding(organizationId: string): Observable<{ url: string }> {
    const base = window.location.origin + window.location.pathname;
    const returnUrl = `${base}?stripe_return=1&orgId=${organizationId}`;
    const refreshUrl = `${base}?stripe_refresh=1&orgId=${organizationId}`;

    return this.api.getStripeOnboardingLink(organizationId, returnUrl, refreshUrl).pipe(
      tap(result => {
        if (result?.url) {
          window.location.href = result.url;
        }
      })
    );
  }

  /**
   * Checks the Stripe account status and updates state accordingly.
   */
  refreshAccountStatus(organizationId: string): Observable<any> {
    return this.api.getStripeAccountStatus(organizationId).pipe(
      tap(result => {
        this.state.setStripeStatus(result.status);
        this.state.checkAndSetComplete();
      })
    );
  }

  /**
   * Parses URL query params to detect a Stripe redirect return.
   * Returns the orgId from the URL if this is a Stripe callback, else null.
   */
  detectStripeReturn(): { isReturn: boolean; isRefresh: boolean; orgId: string | null } {
    const params = new URLSearchParams(window.location.search);
    return {
      isReturn: params.has('stripe_return'),
      isRefresh: params.has('stripe_refresh'),
      orgId: params.get('orgId'),
    };
  }
}
